import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Guard } from "../types";
import { Button } from "@/components/ui/button";
import { Trash2, Loader2 } from "lucide-react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Separator } from "@/components/ui/separator";

interface ImprovedDeleteGuardDialogProps {
  guard: Guard;
  onGuardRemoved?: () => void;
  trigger?: React.ReactNode;
}

export const ImprovedDeleteGuardDialog = ({ guard, onGuardRemoved, trigger }: ImprovedDeleteGuardDialogProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();

  // Don't render if not admin
  if (!isAdmin) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // First clean up any guard equipment associations
      const { error: equipmentError } = await supabase
        .from('guard_equipment')
        .update({ 
          status: 'returned',
          return_date: new Date().toISOString()
        })
        .eq('guard_id', guard.id)
        .eq('status', 'assigned');
      
      if (equipmentError) {
        console.error("Error returning guard equipment:", equipmentError);
      }

      // Try Edge Function first
      const { data, error } = await supabase.functions.invoke('delete-client', {
        body: { clientId: guard.id }
      });

      // If Edge Function fails, use fallback - COMPLETELY WIPE OUT ALL DATA
      if (error || (data && data.error)) {
        console.log('Edge function failed, using fallback delete method - wiping all data');
        
        // Delete from Supabase Auth
        try {
          await supabase.auth.admin.deleteUser(guard.id);
        } catch (authError) {
          console.log('Auth deletion failed:', authError);
        }

        // Delete all related data (ignore errors, continue deleting)
        await supabase.from('training_completions').delete().eq('user_id', guard.id);
        await supabase.from('user_module_progress').delete().eq('user_id', guard.id);
        await supabase.from('user_section_progress').delete().eq('user_id', guard.id);
        await supabase.from('module_user_assignments').delete().eq('user_id', guard.id);
        await supabase.from('assessment_results').delete().eq('user_id', guard.id);
        await supabase.from('shift_swaps').delete().eq('requesting_guard_id', guard.id);
        await supabase.from('shift_swaps').delete().eq('accepting_guard_id', guard.id);
        await supabase.from('guard_evaluations').delete().eq('guard_id', guard.id);
        await supabase.from('guard_complaints').delete().eq('guard_id', guard.id);
        await supabase.from('guard_work_stats').delete().eq('guard_id', guard.id);
        await supabase.from('clock_in_out').delete().eq('guard_id', guard.id);
        await supabase.from('observations').delete().eq('guard_id', guard.id);
        await supabase.from('reports').delete().eq('guard_id', guard.id);
        await supabase.from('schedules').delete().eq('guard_id', guard.id);
        await supabase.from('user_roles').delete().eq('user_id', guard.id);
        await supabase.from('team_members').delete().eq('user_id', guard.id);

        // Finally, delete the profile completely
        const { error: deleteError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', guard.id);

        if (deleteError) {
          console.log('Error deleting profile:', deleteError);
          // If delete fails due to triggers, do soft delete as last resort
          await supabase
            .from('profiles')
            .update({ 
              email: `deleted_${guard.id}@deleted.com`,
              full_name: 'Deleted User'
            })
            .eq('id', guard.id);
        }
      }

      // Close dialog and refresh UI immediately
      setShowDialog(false);
      setIsDeleting(false);
      
      if (onGuardRemoved) {
        onGuardRemoved();
      }

      toast({
        title: "User deleted",
        description: `${guard.first_name} ${guard.last_name} has been permanently deleted from the system.`,
      });
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast({
        variant: "destructive",
        title: "Cannot Delete User",
        description: error.message || "Failed to delete user. Please try again.",
      });
      setIsDeleting(false);
      setShowDialog(false);
    }
  };

  return (
    <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
      {trigger ? (
        <div onClick={(e) => { e.stopPropagation(); setShowDialog(true); }}>
          {trigger}
        </div>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
          onClick={(e) => {
            e.stopPropagation();
            setShowDialog(true);
          }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Permanently Delete User?
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p className="font-semibold">
              You are about to permanently delete {guard.first_name} {guard.last_name}.
            </p>
            <p className="text-sm text-muted-foreground">
              This action <strong>cannot be undone</strong>. All associated data will be permanently removed from the system.
            </p>

            <Separator />
            <div className="space-y-2">
              <p className="text-sm font-semibold">What will be deleted:</p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Authentication credentials</li>
                <li>• Team assignments</li>
                <li>• Assigned equipment (will be marked as returned)</li>
                <li>• Schedule shifts</li>
                <li>• Reports and observations</li>
                <li>• Evaluations and performance data</li>
                <li>• Shift swap requests</li>
                <li>• Training records and certificates</li>
                <li>• Complaints and work stats</li>
                <li>• Clock-in/out records</li>
              </ul>
            </div>
            <Separator />
            <p className="text-sm font-medium text-amber-600">
              Consider archiving instead to preserve historical data while removing access.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Permanently Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};